import { peek } from '@art-suite/art-core-ts';
import { useDroppable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import { useCallback, useMemo, useRef } from 'react';

import { ApiScanNote, ScanNoteType, ScanPartApprovalStatus } from '@kaiizen/kai-pro-api';
import {
  contractorAssignmentChangedDueToChangedDefaults,
  getTotalCostIfDefined,
  getUniqueScanPhotos,
  isOtherTask,
  ScanPhoto,
  ScanTaskOptimized,
  taskHasScopeChanges,
} from '@kaiizen/kai-scan-template-system';

import { DeltaIcon, TrashIcon } from '@/Client/Icons';
import { getTaskTradeForContractorAssignment } from '@/Client/Lib/contractorTradeLib';
import { removeTaskFromScope } from '@/Client/Lib/taskScopeLifecycle';
import { taskNamePresenter } from '@/Client/Presenters';
import { useNavigateToPhoto, usePhotoUriForLayoutSize, useScanEditingContextSlice, useShowNotes } from '@/Client/State';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { useToast } from '@/Client/State/ToastContext';
import { useBulkPhotoUploader } from '@/lib/photoLib';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button, ScanCostDisplayWithDeltaSupport, ScanThumbnail } from '../../Widgets';
import { ScanPartNotesButton } from './ScanPartNotesButton';
import { SimpleContractorPicker } from './SimpleContractorPicker';

const TASK_EDITOR_TITLE_BAR_STYLE = css`
  label: TASK_EDITOR_TITLE_BAR_STYLE;
  display: flex;
  color: var(--text);
  background: var(--background);
  align-items: flex-start;
  padding-bottom: ${LAYOUT2.spacing}px;
`;

const ROW_STYLE_DROP_OVER = css`
  label: ROW_STYLE_DROP_OVER;
  background: var(--textGreen2);
  color: var(--text);
`;

const CELL_STYLE = css`
  padding: 0;
  ${TYPOGRAPHY2.xSmall}
  min-height: 22px;
  display: flex;
  align-items: center;
  padding-top: ${LAYOUT2.spacing}px;
`;

const PHOTO_CELL_STYLE = css`
  ${CELL_STYLE}
  label: PHOTO_CELL_STYLE;
`;

const NAME_CELL_STYLE = css`
  label: NAME_CELL_STYLE;
  ${TYPOGRAPHY2.tinyBold}
  color: var(--text);
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: ${LAYOUT2.spacing}px;
  padding-left: ${LAYOUT2.spacing1}px;
  align-self: stretch;
`;

const NAME_COLUMN_SPACER_STYLE = css`
  label: NAME_COLUMN_SPACER_STYLE;
  flex-grow: 1;
`;

const NAME_CELL_TEXT_STYLE = css`
  ${TYPOGRAPHY2.medium}
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingP5}px;
`;

const COST_CELL_STYLE = css`
  ${CELL_STYLE}
  label: COST_CELL_STYLE;
`;

const ICON_COLUMN_STYLE = css`
  label: ICON_COLUMN_STYLE;
  padding-right: ${LAYOUT2.spacing1 + 1}px;
`;

const CONTRACTOR_PICKER_IN_NAME_COLUMN_STYLE = css`
  label: CONTRACTOR_PICKER_IN_NAME_COLUMN_STYLE;
  width: 250px;
  flex-shrink: 0;
`;

const EXPANDED_TASK_TITLE_BAR_STYLE = css`
  label: EXPANDED_TASK_TITLE_BAR_STYLE;
`;

const TRASH_BUTTON_STYLE = css`
  label: TRASH_BUTTON_STYLE;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;

  transition: all 0.2s ease;
  &:hover {
    transform: scale(1.2);
  }

`;

const CANCEL_CHANGES_LABEL = 'Cancel Task Changes';
const RESOLVE_EDIT_REQUEST_LABEL = 'Resolve Edit Request';

export const InlineTaskEditorTaskBar = ({
  allowedNoteTypes,
  baselineTask,
  expanded,
  groupId,
  approvalStatus,
  notes,
  task,
  toggleInlineTaskEditor,
  taskIsEditRequested,
}: {
  allowedNoteTypes: ScanNoteType[];
  baselineTask?: ScanTaskOptimized;
  expanded: boolean;
  groupId: string;
  approvalStatus: ScanPartApprovalStatus;
  notes: ApiScanNote[];
  task: ScanTaskOptimized;
  toggleInlineTaskEditor: () => void;
  taskIsEditRequested?: boolean;
}) => {
  const taskIsNonStandard = isOtherTask(task);
  const contractorTrade = getTaskTradeForContractorAssignment(task);
  const {
    scanIsEditable,
    scanDataMutators,
    scanId,
    scanRoot,
    baselineScanRoot,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
    showScanChanges,
  } = useScanEditingContextSlice([
    'scanIsEditable',
    'scanDataMutators',
    'scanId',
    'scanRoot',
    'baselineScanRoot',
    'defaultContractorsByTrade',
    'baselineDefaultContractorsByTrade',
    'showScanChanges',
  ]);
  const navigateToPhoto = useNavigateToPhoto();
  const { showNotesEditor } = useShowNotes();
  const { tryWithErrorToast } = useToast();
  const confirm = useConfirmationModal();
  const { setNodeRef, isOver } = useDroppable({
    id: `task-${task.id}`,
    data: {
      type: 'task',
      taskId: task.id,
      groupId: groupId,
      addPhoto: (photo: ScanPhoto) => {
        if (!scanIsEditable) return;
        scanDataMutators.updateTask(groupId, task.id, { photos: getUniqueScanPhotos(task.photos, photo) });
      },
    },
  });
  const photoId = peek(task.optimized.allPhotoIds);
  const taskId = task.id;
  const photoCount = task.optimized.allPhotoIds.length;
  // A 120px square ScanThumbnail: a 4:3 photo's long side spans 120 × 4/3 once cropped to cover it.
  const uri = usePhotoUriForLayoutSize(photoId, 160);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkPhotoUploader = useBulkPhotoUploader();
  const photoSource = task.optimized.allPhotos?.find(photo => photo.photoId === photoId)?.photoSource
    ?? scanRoot?.optimized?.allPhotos?.find(photo => photo.photoId === photoId)?.photoSource;

  const cost = getTotalCostIfDefined(task);
  const baselineCost = getTotalCostIfDefined(baselineTask);

  const hasChanges = useMemo(
    () => showScanChanges && scanIsEditable && taskHasScopeChanges(task, baselineTask ?? null, defaultContractorsByTrade, baselineDefaultContractorsByTrade),
    [scanIsEditable, task, showScanChanges, baselineTask, defaultContractorsByTrade, baselineDefaultContractorsByTrade],
  );

  const undoChanges = useCallback(() => {
    if (!scanIsEditable) return;
    if (!baselineTask || !baselineScanRoot) return;
    const contractorAssignmentChangedBecauseOfDefaults = contractorAssignmentChangedDueToChangedDefaults(
      task,
      baselineTask,
      defaultContractorsByTrade,
      baselineDefaultContractorsByTrade,
    );
    confirm({
      title: CANCEL_CHANGES_LABEL,
      message: 'Are you sure you want to cancel the changes to this task?',
      onConfirm: () => {
        tryWithErrorToast('cancel changes to task', () => scanDataMutators.revertTaskScopeChanges(baselineScanRoot, groupId, task.id))
          .then(() => {
            if (contractorAssignmentChangedBecauseOfDefaults) {
              window.alert(
                "Can't cancel changes here because the default contractor assignment changed. Please cancel the default contractor changes on the project page.",
              );
            }
          });
      },
    });
  }, [
    groupId,
    task,
    baselineTask,
    baselineScanRoot,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
    scanIsEditable,
    scanDataMutators,
    confirm,
    tryWithErrorToast,
  ]);

  const resolveEditRequest = () => {
    // show notes and allow resolved-edit note types
    showNotesEditor({ scanPart: task, allowedNoteTypes: ['info', 'editResolution'] });
  };

  const deleteTask = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!scanIsEditable) return;
    confirm({
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task?',
      onConfirm: () => removeTaskFromScope(scanDataMutators, groupId, task.id),
    });
  };

  return (
    <div
      ref={setNodeRef}
      css={[
        TASK_EDITOR_TITLE_BAR_STYLE,
        expanded && EXPANDED_TASK_TITLE_BAR_STYLE,
        isOver && ROW_STYLE_DROP_OVER,
      ]}
      onClick={toggleInlineTaskEditor}
    >
      <div css={PHOTO_CELL_STYLE}>
        <ScanThumbnail
          uri={uri}
          photoCount={photoCount}
          photoSource={photoSource}
          onClick={() => photoId ? navigateToPhoto({ groupId, taskId, photoId }) : (scanIsEditable ? fileInputRef.current?.click() : undefined)}
        />
        {scanIsEditable && (
          <input
            ref={fileInputRef}
            type={'file'}
            accept={'image/*'}
            multiple
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={async e => {
              await bulkPhotoUploader(e.target.files, task, { groupId, taskId });
              e.target.value = '';
            }}
          />
        )}
      </div>
      <div css={NAME_CELL_STYLE}>
        <div css={NAME_CELL_TEXT_STYLE}>
          {hasChanges && <DeltaIcon size={TYPOGRAPHY2.fontSize.medium} color={COLORS2.alwaysDark.red} />}
          {taskNamePresenter(task)}
          {hasChanges && showScanChanges && <Button buttonStyle={'yellow'} size={'small'} onClick={undoChanges}>{CANCEL_CHANGES_LABEL}</Button>}
          {taskIsEditRequested && <Button buttonStyle={'yellow'} size={'small'} onClick={resolveEditRequest}>{RESOLVE_EDIT_REQUEST_LABEL}</Button>}
        </div>
        <div css={NAME_COLUMN_SPACER_STYLE}></div>
        <div css={CONTRACTOR_PICKER_IN_NAME_COLUMN_STYLE}>
          <SimpleContractorPicker
            backgroundColor='var(--background)'
            assignedContractorId={task.assignedContractorId || ''}
            baselineAssignedContractorId={baselineTask?.assignedContractorId || ''}
            updateAssignedContractorId={contractorId => {
              if (!scanIsEditable) return;
              scanDataMutators.updateTask(groupId, task.id, { assignedContractorId: contractorId });
            }}
            trade={contractorTrade}
            filterByTrade={false}
            labelOverride={'Contractor'}
            disabled={!scanIsEditable}
          />
        </div>
      </div>
      <div css={COST_CELL_STYLE}>
        <ScanCostDisplayWithDeltaSupport
          cost={cost}
          baselineCost={baselineCost}
          showBaseline={!!(showScanChanges && baselineTask !== task)}
        />
      </div>
      <div css={ICON_COLUMN_STYLE}>
        <ScanPartNotesButton
          scanPart={task}
          notes={notes}
          allowedNoteTypes={allowedNoteTypes}
          approvalStatus={approvalStatus}
        />
        {scanIsEditable && taskIsNonStandard
          && (
            <div css={TRASH_BUTTON_STYLE} onClick={deleteTask}>
              <TrashIcon size={40} />
            </div>
          )}
      </div>
    </div>
  );
};
